import type { SceneObject } from '../../exports/scene';
import { geometrySource, restoreGeometrySources } from './geometry-source';
import { geometryCost } from './geometry-cost';
import { unusedMeshAssetName } from './unused-mesh-asset-name';

export interface ReuseMeshOptions {
    /** Hosts that persist unloaded scenes can stamp src here instead of loading it. */
    assignSource?: (object: SceneObject, src: string) => Promise<void>;
    onSceneChanged?: () => void;
}

/** Explicit asset assignment: no code generation and no implicit update of siblings. */
export async function reuseMesh(source: SceneObject, objects: SceneObject[], options: ReuseMeshOptions = {}): Promise<string> {
    const changed: string[] = [];
    let attempting: string | null = null;
    try {
        const scene = source.traceScene;
        const live = new Set(scene.getSceneObjects());
        if (!live.has(source)) throw new Error('source must be a live baked mesh object.');
        if (!Array.isArray(objects) || !objects.length || objects.length > 128 || objects.some(object => !live.has(object))) {
            throw new Error('objects must contain 1–128 live objects in the source scene.');
        }
        const asset = (object: SceneObject) => object.src ? scene.resources.getAsset(object.src) : undefined;
        const targets = [...new Set(objects)];
        await restoreGeometrySources(scene, [source]);
        const blob = asset(source);
        if (!blob || !geometrySource(scene, source)) throw new Error('source must have recoverable build_mesh geometry. This tool copies one baked asset, not an authored subtree.');
        // Register these exact bytes locally, rather than copying a property ref or
        // a path whose base might belong to a different imported document.
        const fileName = unusedMeshAssetName(scene.resources, 'reused-mesh');
        const key = await scene.resources.addAssetWithDedup(`assets/${fileName}`, blob);
        if (asset(source) !== blob) throw new Error('source asset changed during inspection; inspect it again.');
        const src = `./${key}`;
        const unchanged: string[] = [];
        for (const object of targets) {
            if (asset(object) === blob) { unchanged.push(object.getPath()); continue; }
            attempting = object.getPath();
            if (options.assignSource) await options.assignSource(object, src);
            else await object.setSrc(src);
            changed.push(object.getPath());
            attempting = null;
        }
        const retainedGeometry = targets.flatMap(object => {
            const cost = geometryCost(scene, object);
            const branches = cost.largestBranches.filter(branch => branch.path !== object.getPath());
            return branches.length ? [{target: object.getPath(), branches, omittedBranches: cost.omittedBranches}] : [];
        });
        return JSON.stringify({ source: source.getPath(), changed, unchanged,
            retainedGeometry,
            retainedGeometryScope: 'Observed loaded visible indexed triangle geometry in child branches, with at most five largest branches per target. Other branches may be omitted; unsupported or unloaded geometry is not certified absent.',
            ...(retainedGeometry.length ? {warning: 'Observed retained child geometry remains in the scene. Asset assignment does not replace their subtrees; inspect the listed paths before treating this as a complete geometry replacement.'} : {}),
            requestedCount: targets.length, changedCount: changed.length, unchangedCount: unchanged.length,
            scope: 'Assigned the same baked asset to the explicitly named owners. Identity, transform, parent, activation, authored behaviors and children are preserved. Existing material-slot overrides remain; inspect them against the new asset. No other copies updated and no live link to future revisions.' });
    } catch (error) {
        return `Error: ${error instanceof Error ? error.message : String(error)}\nAlready updated objects: ${JSON.stringify(changed)}.${attempting ? ` Failed target may be partially changed: ${attempting}.` : ''} Inspect before retrying.`;
    } finally {
        if (changed.length || attempting) options.onSceneChanged?.();
    }
}
